import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { fetchAppointmentInvoiceLinks } from '@/hooks/useAppointmentInvoicing';

export type AppointmentStatus = Database['public']['Enums']['appointment_status'];
export type PaymentStatus = Database['public']['Enums']['payment_status'];
export type VisitStatus = Database['public']['Enums']['visit_status'];

export interface PatientAppointmentItem {
  id: string;
  clinic_id: string;
  patient_id: string;
  clinician_id: string | null;
  clinician_name: string | null;
  service_id: string | null;
  service_name: string | null;
  service_color: string | null;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  visit_status: VisitStatus | null;
  payment_status: PaymentStatus | null;
  payment_amount_cents: number | null;
  payment_currency: string | null;
  payment_method: string | null;
  payment_intent_id: string | null;
  notes: string | null;
  source: string | null;
  created_at: string;
  video_meeting_url: string | null;
  invoice_id: string | null;
  invoice_number: string | null;
}

export const patientAppointmentsQueryKey = (patientId: string | null) =>
  ['appointments', 'patient', patientId] as const;

export function usePatientAppointments(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientAppointmentsQueryKey(patientId ?? null),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientAppointmentItem[]> => {
      if (!patientId) return [];
      
      // First, fetch all appointments for this patient
      const { data, error } = await supabase
        .from('appointments')
        .select(
          'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, visit_status, ' +
          'payment_status, payment_amount_cents, payment_currency, payment_method, payment_intent_id, notes, source, created_at, video_meeting_url, ' +
          'clinician:users!appointments_clinician_id_fkey(name,email), ' +
          'service:services!appointments_service_id_fkey(color)'
        )
        .eq('patient_id', patientId)
        .order('starts_at', { ascending: false });

      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        patient_id: string;
        clinician_id: string | null;
        service_id: string | null;
        service_name: string | null;
        starts_at: string;
        ends_at: string;
        status: AppointmentStatus;
        visit_status: VisitStatus | null;
        payment_status: PaymentStatus | null;
        payment_amount_cents: number | null;
        payment_currency: string | null;
        payment_method: string | null;
        payment_intent_id: string | null;
        notes: string | null;
        source: string | null;
        created_at: string;
        video_meeting_url: string | null;
        clinician: { name: string | null; email: string } | null;
        service: { color: string | null } | null;
      };

      const appointmentIds = ((data ?? []) as unknown as Joined[]).map((r) => r.id);
      const invoiceLinks = await fetchAppointmentInvoiceLinks(appointmentIds);

      return ((data ?? []) as unknown as Joined[]).map(
        (r): PatientAppointmentItem => {
          const link = invoiceLinks.get(r.id);
          const invoicePaid = link?.invoice_status === 'paid';
          const invoiceRefunded = link?.invoice_status === 'refunded';
          const resolvedStatus: PaymentStatus | null = r.payment_status
            ? r.payment_status
            : invoicePaid
              ? 'paid'
              : invoiceRefunded
                ? 'refunded'
                : r.visit_status === 'payment_completed'
                  ? 'paid'
                  : null;
          const resolvedMethod =
            r.payment_method || link?.payment_method || (invoicePaid ? 'invoice' : null);
          return {
            id: r.id,
            clinic_id: r.clinic_id,
            patient_id: r.patient_id,
            clinician_id: r.clinician_id,
            clinician_name: r.clinician?.name ?? r.clinician?.email ?? null,
            service_id: r.service_id,
            service_name: r.service_name,
            service_color: r.service?.color ?? null,
            starts_at: r.starts_at,
            ends_at: r.ends_at,
            status: r.status,
            visit_status: r.visit_status,
            payment_status: resolvedStatus,
            payment_amount_cents: r.payment_amount_cents ?? (invoicePaid ? link?.total_cents ?? null : null),
            payment_currency: r.payment_currency ?? link?.currency ?? null,
            payment_method: resolvedMethod,
            payment_intent_id: r.payment_intent_id,
            notes: r.notes,
            source: r.source,
            created_at: r.created_at,
            video_meeting_url: r.video_meeting_url,
            invoice_id: link?.invoice_id ?? null,
            invoice_number: link?.invoice_number ?? null,
          };
        },
      );
    },
    staleTime: 15_000,
  });
}
